import {
  Button,
  Divider,
  Image,
  InlineStack,
  OptionCard,
  OptionCardDescription,
  OptionCardGroup,
  OptionCardTitle,
  Stack,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import type { SqlDatabaseType, SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import { SQL_DATABASE_CATALOG } from '@/features/sql-sync';
import { databaseAddress, databaseTitle } from '../database-form';

const IN_USE_TOOLTIP = "You cannot delete a source that's currently being used by a SQL Sync.";

type DatabaseListProps = {
  type: SqlDatabaseType;
  databases: readonly SqlImportDatabase[];
  selectedId: string | undefined;
  isReadOnly: boolean;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onEdit: (database: SqlImportDatabase) => void;
  onDelete: (database: SqlImportDatabase) => void;
};

/** The workspace's saved databases for this engine, one selectable card each, plus the way to add another. */
export function DatabaseList({
  type,
  databases,
  selectedId,
  isReadOnly,
  onSelect,
  onAdd,
  onEdit,
  onDelete,
}: DatabaseListProps) {
  return (
    <>
      <OptionCardGroup
        label="Which source do you want to sync from?"
        inputType="radio"
        value={selectedId ?? null}
        onChange={(value: string) => onSelect(value)}
        cardsDirection="column"
        cardsGap="150"
      >
        {databases.map((database) => (
          <OptionCard
            key={database.id}
            value={database.id}
            ariaLabel={databaseTitle(database)}
            data-test-create-sql-import-database-option-card={databaseTitle(database)}
          >
            <InlineStack alignBlock="stretch" w="full">
              <Stack alignBlock="center" p="300">
                <Image src={SQL_DATABASE_CATALOG[type].imageSrc} size="icon-v2-xl" alt="" />
              </Stack>
              <Divider direction="vertical" />
              <InlineStack flex={1} alignBlock="center" alignInline="space-between" p="300">
                <Stack alignInline="start" gap="050">
                  <OptionCardTitle>{databaseTitle(database)}</OptionCardTitle>
                  {databaseAddress(database) == null ? null : (
                    <OptionCardDescription>{databaseAddress(database)}</OptionCardDescription>
                  )}
                </Stack>
                <InlineStack gap="050" alignBlock="center">
                  <Button
                    variant="secondary"
                    size="sm"
                    isDisabled={isReadOnly}
                    onClick={() => onEdit(database)}
                    data-test-sql-import-button="edit-database"
                  >
                    Edit
                  </Button>
                  <Tooltip isDisabled={database.in_use !== true} isCustom={true}>
                    {(context) => (
                      <>
                        <Button
                          ref={context.reference.setReference}
                          {...context.reference.getReferenceProps()}
                          variant="secondary"
                          size="sm"
                          icon="delete-trash"
                          isIconOnly
                          aria-label={`Delete ${databaseTitle(database)}`}
                          isDisabled={database.in_use === true || isReadOnly}
                          onClick={() => onDelete(database)}
                          data-test-sql-import-button="delete-database"
                        />
                        <TooltipContent>{IN_USE_TOOLTIP}</TooltipContent>
                      </>
                    )}
                  </Tooltip>
                </InlineStack>
              </InlineStack>
            </InlineStack>
          </OptionCard>
        ))}
      </OptionCardGroup>
      <InlineStack alignBlock="stretch" alignInline="end" mt="200">
        <Button variant="secondary" isDisabled={isReadOnly} onClick={onAdd} data-test-sql-import-button="add-database">
          Add database
        </Button>
      </InlineStack>
    </>
  );
}
